import React, { useRef } from 'react'
import { useDispatch } from 'react-redux'
import { useMutation, useQueryClient } from 'react-query'
import PropTypes from 'prop-types'
import { useDrag, useDrop } from 'react-dnd'
import { push } from 'react-router-redux'
import moment from 'moment'
import styled from 'styled-components'
import withApiData from '../../HOC/withApiData'
import PublishLabel from '../../mini/MyCourses/PublishLabel'
import { showModal } from '../../../actions/modal'
import { ItemTypes } from '../Journey/ItemTypes'
import _SortOrderIcon from '../../../static/images/mycourses/SortOrder.svg'
import EditOrDeleteMenuButton from '../../mini/Buttons/EditOrDeleteMenuButton'
import P2 from '../../typography/paragraphs/P2'
import H5 from '../../typography/headers/H5'
import { deleteLevelActivity, reorderActivities } from './API'
import colors from '../../../configuration/colors'
import FixedAlignmentBox from '../../mini/Layout/FixedAlignmentBox'

const JourneyContainer = styled.div`
  margin: 11px 0;
  background: none;
`

const FlexContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-content: center;
  align-items: center;
`

const JourneyInfoContainer = styled.div`
  display: flex;
  justify-content: space-between;
  padding: ${props => (props.hasKebab
    ? '4px 10px'
    : '10px 22px 10px 10px')};
  border: 1px solid #dedfe0;
  border-radius: 4px;
  background-color: #ffffff;
  flex: 90%;
  align-content: center;
  align-items: center;
`

const SortIcon = styled.img.attrs({
  src: _SortOrderIcon
})`
  width: 19px;
  height: 9px;
  margin-right: 15px;
`

const ActivityIconContainer = styled.div`
  width: 32px;
  height: 32px;
  background-color: ${colors.ash};
  border-radius: 4px;
  border: 0.5px solid ${colors.rock};
  margin-right: 15px;
  position: relative;
`

const ActivityRow = ({
  apiData,
  activity,
  levelId,
  levels,
  journeyId,
  courseId,
  moveCard,
  index,
  id,
  handleShouldExpandLevel,
  isReadyForEnrollment
}) => {
  const dispatch = useDispatch()
  const queryClient = useQueryClient()

  const ref = useRef(null)
  const [{ handlerId }, drop] = useDrop({
    accept: ItemTypes.ACTIVITY_ROW,
    collect(monitor) {
      return {
        handlerId: monitor.getHandlerId()
      }
    },
    hover(item, monitor) {
      if (!ref.current) {
        return
      }
      const dragIndex = item.index
      const hoverIndex = index
      // Don't replace items with themselves
      if (dragIndex === hoverIndex) {
        return
      }
      // Determine rectangle on screen
      const hoverBoundingRect = ref.current.getBoundingClientRect()
      // Get vertical middle
      const hoverMiddleY =
        (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2
      // Determine mouse position
      const clientOffset = monitor.getClientOffset()
      // Get pixels to the top
      const hoverClientY = clientOffset.y - hoverBoundingRect.top
      // Only perform the move when the mouse has crossed half of the items height
      // When dragging downwards, only move when the cursor is below 50%
      // When dragging upwards, only move when the cursor is above 50%
      // Dragging downwards
      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
        return
      }
      // Dragging upwards
      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
        return
      }
      // Time to actually perform the action
      moveCard(dragIndex, hoverIndex)
      // Note: we're mutating the monitor item here!
      // Generally it's better to avoid mutations,
      // but it's good here for the sake of performance
      // to avoid expensive index searches.
      item.index = hoverIndex
    },
    drop(item) {
      const index = item.index - 1 === -1 ? 0 : item.index
      reorderActivities(apiData, levelId, id, index)
    }
  })
  const [{ isDragging }, drag] = useDrag({
    type: ItemTypes.ACTIVITY_ROW,
    item: () => {
      return { id, index }
    },
    collect: monitor => ({
      isDragging: monitor.isDragging()
    })
  })

  const opacity = isDragging ? 0 : 1
  drag(drop(ref))

  const deleteMutation = useMutation(({ apiData, levelId, activityId }) =>
    deleteLevelActivity(apiData, levelId, activityId)
  )
  const onDeleteClick = (apiData, levelId, activityId) => {
    deleteMutation.mutate(
      {
        apiData,
        levelId,
        activityId
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries('level-activities-' + levelId)
          queryClient.invalidateQueries('journey-levels-' + journeyId)
        }
      }
    )
  }

  const handleClick = e => {
    dispatch(
      push(
        `/mycourses/${courseId}/journey/${journeyId}/level/${levelId}/activityQuest/${activity.id}`
      )
    )
    e.stopPropagation()
  }

  const handleClose = option => {
    if (option === 'Delete') {
      dispatch(
        showModal('CLOSE_MODAL', {
          description: 'Are you sure you want to delete this activity?',
          supportiveText: 'Once deleted, this cannot be undone.',
          isOptionalTextExist: false,
          deleteFunction: () => onDeleteClick(apiData, levelId, activity.id)
        })
      )
    }

    if (option === 'Level Placement') {
      dispatch(
        showModal('CHANGE_LEVEL_FOR_ACTIVITY_MODAL', {
          levels,
          currentLevelId: levelId,
          activityQuestId: activity.id,
          activityQuestName: activity.name,
          handleShouldExpandLevel,
          courseId,
          journeyId
        })
      )
    }
  }

  return (
    <div style={{ boxShadow: 'none' }} ref={!isReadyForEnrollment ? ref : null}>
      <JourneyContainer>
        <FlexContainer>
          {!isReadyForEnrollment && (
            <SortIcon />
          )}
          <JourneyInfoContainer hasKebab={!isReadyForEnrollment}>
            <ActivityIconContainer>
              {activity.icon_image_url !== null && (
                <img
                  src={activity.icon_image_url}
                  style={{
                    width: '32px',
                    height: '32px',
                    position: 'absolute'
                  }}
                />
              )}
            </ActivityIconContainer>
            <H5
              style={{ flex: '30%' }}
              onClick={handleClick}
            >
              {activity.name}
            </H5>
            <P2
              style={{ marginRight: '15px' }}
              onClick={handleClick}
            >
              <span>Last saved: </span>
              {moment
                .unix(activity.updated_at)
                .format('DD MMMM YYYY')
                .toString()}
            </P2>
            <FixedAlignmentBox justify="center" width="86px"
              onClick={handleClick}
            >
              <PublishLabel isPublished={!activity.in_draft} />
            </FixedAlignmentBox>
            {!isReadyForEnrollment && (
              <EditOrDeleteMenuButton
                options={['Delete', 'Level Placement']}
                handleChosenOption={handleClose}
              />
            )}
          </JourneyInfoContainer>
        </FlexContainer>
      </JourneyContainer>
    </div>
  )
}

ActivityRow.propTypes = {
  apiData: PropTypes.object.isRequired,
  activity: PropTypes.object.isRequired,
  levelId: PropTypes.number.isRequired,
  levels: PropTypes.array.isRequired,
  courseId: PropTypes.string.isRequired,
  moveCard: PropTypes.func.isRequired,
  index: PropTypes.number.isRequired,
  id: PropTypes.number.isRequired,
  handleShouldExpandLevel: PropTypes.func.isRequired,
  isReadyForEnrollment: PropTypes.bool
}

export default withApiData(ActivityRow)



// WEBPACK FOOTER //
// ./src/components/mycourses/Activities/ActivityRow.js